import { Button, Icon, IconName, IconSize } from '@unmind/design-system';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import RoutePath from 'App/RoutePath';
import { tracking } from 'App/Tracking';

import { TalkModal } from './../TalkModal';
import { default as HeroBannerImage } from './assets/hero-banner.jpg';
import { Banner } from './styles';

export default ({
  entitlements: {
    hasCounselling,
    hasCounsellingAppointment,
    hasTalk,
    hasMatching,
  },
  sessions: { remainingSessions, sessionLimit, sessionsExpired },
}: {
  entitlements: {
    hasCounselling: boolean;
    hasCounsellingAppointment: boolean;
    hasTalk: boolean;
    hasMatching: boolean;
  };
  sessions: {
    remainingSessions?: number;
    sessionLimit?: number;
    sessionsExpired?: boolean;
  };
}) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'book',
  });
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const modalLaunchRef = useRef<HTMLButtonElement>(null);

  if (!hasCounselling && !hasTalk) {
    return <></>;
  }

  const bannerTextPath: 'banner.talk' | 'banner.counselling' = `banner.${
    hasTalk ? 'talk' : 'counselling'
  }`;
  let bannerPill: string | React.ReactNode = (
    <>
      <Icon icon="call" /> <Icon icon="video-on" />{' '}
      {t(`${bannerTextPath}.pill`)}
    </>
  );
  let bannerContent: string | React.ReactNode = t(`${bannerTextPath}.content`);
  let cta: React.ReactNode = (
    <Button
      label={t(
        `banner.counselling.cta.${
          hasCounsellingAppointment ? 'appointment' : 'default'
        }`,
      )}
      variant="primary"
      styling="full-width"
      to={RoutePath.BookService.replace(':service', 'counselling')}
      onClick={() =>
        tracking.track('book_hero_banner_clicked', { button: 'counselling' })
      }
    />
  );

  if (hasTalk) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pillPath: any = `${bannerTextPath}.pill${
      sessionLimit === 52 ? '_weekly' : ''
    }`;

    const hasAllowanceToShow =
      !sessionsExpired &&
      remainingSessions !== undefined &&
      sessionLimit !== undefined;

    bannerPill = hasAllowanceToShow ? (
      <>
        <Icon icon="video-on" /> {t(pillPath, { count: remainingSessions })}
      </>
    ) : undefined;

    bannerContent = (
      <>
        <p>{t(`${bannerTextPath}.content`)}</p>
        <button
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowModal(true);
          }}
        >
          <Icon
            iconName={IconName.InformationCircleSimple}
            size={IconSize.Small}
          />
          {t(`banner.talk.modal.launchText`)}
        </button>
        <TalkModal
          onClose={() => setShowModal(false)}
          isOpen={showModal}
          parentRef={modalLaunchRef}
        />
      </>
    );

    cta = (
      <Button
        label={
          <>
            <Icon
              iconName={IconName.MagnifyingGlass}
              colors={{
                primary:
                  theme.button[hasMatching ? 'secondary' : 'primary'].default
                    .textColor,
              }}
            />
            {t('banner.talk.cta.browse')}
          </>
        }
        variant={hasMatching ? 'secondary' : 'primary'}
        to={RoutePath.BookPractitioners}
        onClick={() =>
          tracking.track('book_hero_banner_clicked', {
            button: 'browse_practitioners',
          })
        }
        data-cy="talk-banner-browse-practitioners"
      />
    );

    if (hasMatching) {
      cta = (
        <ul>
          <li>
            <Button
              label={
                <>
                  <Icon
                    iconName={IconName.Sparkle}
                    colors={{
                      primary: theme.button.primary.default.textColor,
                    }}
                  />
                  {t('banner.talk.cta.match')}
                </>
              }
              variant="primary"
              to={RoutePath.BookMatch}
              onClick={() =>
                tracking.track('book_hero_banner_clicked', {
                  button: 'find_match',
                })
              }
            />
          </li>
          <li>{cta}</li>
        </ul>
      );
    }
  }

  return (
    <Banner
      content={bannerContent}
      cta={cta}
      image={HeroBannerImage}
      pill={bannerPill}
      title={t(`${bannerTextPath}.title`)}
    />
  );
};
